import { Controller } from "@hotwired/stimulus"

// "바탕화면에 설치" (PWA).
//
// 크롬 계열은 설치 조건(HTTPS + 매니페스트 + fetch 핸들러가 있는 서비스워커)이
// 갖춰지면 beforeinstallprompt 이벤트를 준다. 그걸 잡아 두었다가 버튼을 누를 때
// prompt() 를 띄운다.
//
// 설치되지 않은 동안에는 버튼을 늘 보여 준다. 이벤트를 주지 않는 브라우저
// (사파리·파이어폭스)에서는 눌렀을 때 그 브라우저의 설치 경로를 안내한다 —
// 버튼이 아예 없으면 사용자는 설치가 가능한지조차 알 수 없다.
//
// 이미 설치돼 있으면 안내 영역째 숨긴다. 설치 여부는 세 가지로 알아낸다.
//   - 설치된 앱 창(standalone)으로 열렸다
//   - getInstalledRelatedApps() 가 이 앱을 돌려준다 (크롬 계열, 매니페스트의
//     related_applications 가 자기 자신을 가리켜야 한다)
//   - 이 브라우저에서 설치한 기록이 남아 있다 (appinstalled 때 저장)
// 앱을 지우면 크롬이 beforeinstallprompt 를 다시 주므로, 그때 기록을 지우고
// 버튼을 되살린다.
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

const INSTALLED_KEY = "foodglory:pwa-installed"

export default class extends Controller<HTMLElement> {
  static targets = ["button", "hint"]

  declare readonly buttonTarget: HTMLButtonElement
  declare readonly hintTarget: HTMLElement
  declare readonly hasButtonTarget: boolean
  declare readonly hasHintTarget: boolean

  private deferred?: InstallPromptEvent

  private onBeforeInstall = (event: Event): void => {
    event.preventDefault()
    this.deferred = event as InstallPromptEvent
    this.forgetInstalled()
    this.element.hidden = false
    this.setHint("바탕화면에 바로가기를 만들어 브라우저 주소창 없이 쓸 수 있습니다.")
  }

  private onInstalled = (): void => {
    this.deferred = undefined
    this.rememberInstalled()
    this.markInstalled()
  }

  connect(): void {
    window.addEventListener("beforeinstallprompt", this.onBeforeInstall)
    window.addEventListener("appinstalled", this.onInstalled)

    if (this.standalone() || this.installedBefore()) {
      this.markInstalled()
      return
    }

    this.setHint(this.manualHint())
    void this.detectInstalledApp()
  }

  disconnect(): void {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstall)
    window.removeEventListener("appinstalled", this.onInstalled)
  }

  async install(): Promise<void> {
    if (!this.deferred) {
      // 아직 설치 조건이 갖춰지지 않았거나 지원하지 않는 브라우저
      this.setHint(this.manualHint())
      return
    }

    await this.deferred.prompt()
    const { outcome } = await this.deferred.userChoice
    this.deferred = undefined

    if (outcome === "accepted") {
      this.setHint("설치를 진행합니다…")
    } else {
      this.setHint("설치를 취소했습니다. 필요할 때 다시 눌러 주세요.")
    }
  }

  private markInstalled(): void {
    this.element.hidden = true
  }

  private async detectInstalledApp(): Promise<void> {
    const nav = window.navigator as unknown as {
      getInstalledRelatedApps?: () => Promise<unknown[]>
    }
    if (typeof nav.getInstalledRelatedApps !== "function") return

    try {
      const apps = await nav.getInstalledRelatedApps()
      if (apps.length > 0 && !this.deferred) this.markInstalled()
    } catch {
      // 지원하지 않거나 막힌 환경 — 버튼을 그대로 둔다
    }
  }

  // 설치 기록은 이 브라우저에만 남기는 편의용이다. 저장소가 막혀 있어도
  // 버튼이 보이는 쪽으로 동작하면 그만이다.
  private installedBefore(): boolean {
    try {
      return window.localStorage.getItem(INSTALLED_KEY) === "1"
    } catch {
      return false
    }
  }

  private rememberInstalled(): void {
    try { window.localStorage.setItem(INSTALLED_KEY, "1") } catch { /* 무시 */ }
  }

  private forgetInstalled(): void {
    try { window.localStorage.removeItem(INSTALLED_KEY) } catch { /* 무시 */ }
  }

  private setHint(text: string): void {
    if (this.hasHintTarget) this.hintTarget.textContent = text
  }

  private standalone(): boolean {
    return window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
  }

  // 네이티브 프롬프트를 못 쓰는 브라우저용 안내
  private manualHint(): string {
    const ua = window.navigator.userAgent
    if (/iPhone|iPad|iPod/.test(ua)) {
      return "사파리에서 공유 버튼 → '홈 화면에 추가' 를 눌러 주세요."
    }
    if (/Firefox/.test(ua)) {
      return "파이어폭스는 설치를 지원하지 않습니다. 크롬이나 엣지에서 이용해 주세요."
    }
    if (/Edg\//.test(ua)) {
      return "엣지 주소창 오른쪽의 설치 아이콘으로도 설치할 수 있습니다."
    }
    return "크롬 주소창 오른쪽의 설치 아이콘 또는 ⋮ 메뉴 → '앱 설치' 로도 설치할 수 있습니다."
  }
}
